/**
 * Sound utility for playing celebration sounds
 * Uses Web Audio API which works on both web and native platforms
 */

let audioContext: AudioContext | null = null;

/**
 * Initialize audio context (required for Web Audio API)
 */
const getAudioContext = (): AudioContext => {
	if (!audioContext) {
		// Create audio context - works on both web and native
		audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
	}
	return audioContext;
};

/**
 * Generate different types of celebration sounds
 * Each function creates a unique celebratory sound
 */

// Sound 1: Confetti Pop
const generateConfettiPop = (_ctx: AudioContext, _duration: number, sampleRate: number, numSamples: number): Float32Array => {
	const data = new Float32Array(numSamples);
	let filter = 0;

	for (let i = 0; i < numSamples; i++) {
		const t = i / sampleRate;
		const envelope = Math.exp(-t * 10);
		const noise = Math.random() * 2 - 1;
		filter = filter * 0.7 + noise * 0.3;
		const pop = Math.sin(2 * Math.PI * (800 - t * 200) * t) * envelope;
		data[i] = (filter + pop) * envelope * 0.4;
	}
	return data;
};

// Sound 2: Fireworks Burst
const generateFireworks = (_ctx: AudioContext, _duration: number, sampleRate: number, numSamples: number): Float32Array => {
	const data = new Float32Array(numSamples);

	for (let i = 0; i < numSamples; i++) {
		const t = i / sampleRate;
		const envelope = Math.exp(-t * 2.5);
		const whoosh = Math.sin(2 * Math.PI * (300 - t * 150) * t) * envelope * 0.3;
		const crackle = (Math.random() * 2 - 1) * envelope * 0.5;
		const sparkle = Math.sin(2 * Math.PI * (1500 + Math.sin(t * 30) * 200) * t) * envelope * 0.2;
		data[i] = (whoosh + crackle + sparkle) * 0.4;
	}
	return data;
};

// Sound 3: Balloon Pop
const generateBalloonPop = (_ctx: AudioContext, _duration: number, sampleRate: number, numSamples: number): Float32Array => {
	const data = new Float32Array(numSamples);

	for (let i = 0; i < numSamples; i++) {
		const t = i / sampleRate;
		if (t > 0.2) {
			data[i] = 0;
			continue;
		}
		const envelope = Math.exp(-t * 20);
		const pop = Math.sin(2 * Math.PI * (1200 - t * 500) * t) * envelope;
		const snap = (Math.random() * 2 - 1) * envelope * 0.3;
		data[i] = (pop + snap) * envelope * 0.5;
	}
	return data;
};

// Sound 4: Crowd Clapping
const generateCrowdClap = (_ctx: AudioContext, _duration: number, sampleRate: number, numSamples: number): Float32Array => {
	const data = new Float32Array(numSamples);

	for (let i = 0; i < numSamples; i++) {
		const t = i / sampleRate;
		// Multiple claps at different times
		const clap1 = t > 0.05 && t < 0.15 ? Math.exp(-(t - 0.1) * 50) : 0;
		const clap2 = t > 0.1 && t < 0.2 ? Math.exp(-(t - 0.15) * 50) : 0;
		const clap3 = t > 0.15 && t < 0.25 ? Math.exp(-(t - 0.2) * 50) : 0;
		const noise = (Math.random() * 2 - 1) * 0.3;
		const envelope = Math.exp(-t * 1.5);
		data[i] = (clap1 + clap2 + clap3 + noise) * envelope * 0.4;
	}
	return data;
};

// Sound 5: Party Horn
const generatePartyHorn = (_ctx: AudioContext, _duration: number, sampleRate: number, numSamples: number): Float32Array => {
	const data = new Float32Array(numSamples);

	for (let i = 0; i < numSamples; i++) {
		const t = i / sampleRate;
		const envelope = Math.exp(-t * 1.2);
		const freq = 400 + t * 200; // Rising frequency
		const horn = Math.sin(2 * Math.PI * freq * t) * envelope;
		const vibrato = Math.sin(2 * Math.PI * 5 * t) * 0.1;
		data[i] = horn * (1 + vibrato) * envelope * 0.4;
	}
	return data;
};

// Sound 6: Celebration Chime
const generateCelebrationChime = (_ctx: AudioContext, _duration: number, sampleRate: number, numSamples: number): Float32Array => {
	const data = new Float32Array(numSamples);

	for (let i = 0; i < numSamples; i++) {
		const t = i / sampleRate;
		const envelope = Math.exp(-t * 1.5);
		// Multiple tones (chord)
		const tone1 = Math.sin(2 * Math.PI * 523.25 * t) * envelope; // C5
		const tone2 = Math.sin(2 * Math.PI * 659.25 * t) * envelope * 0.7; // E5
		const tone3 = Math.sin(2 * Math.PI * 783.99 * t) * envelope * 0.5; // G5
		data[i] = (tone1 + tone2 + tone3) * envelope * 0.25;
	}
	return data;
};

// Sound 7: Success Fanfare
const generateSuccessFanfare = (_ctx: AudioContext, _duration: number, sampleRate: number, numSamples: number): Float32Array => {
	const data = new Float32Array(numSamples);

	for (let i = 0; i < numSamples; i++) {
		const t = i / sampleRate;
		const envelope = Math.exp(-t * 1);
		// Ascending notes
		const note1 = t < 0.2 ? Math.sin(2 * Math.PI * 440 * t) * Math.exp(-(t - 0.1) * 30) : 0; // A4
		const note2 = t > 0.1 && t < 0.3 ? Math.sin(2 * Math.PI * 523.25 * (t - 0.1)) * Math.exp(-(t - 0.2) * 30) : 0; // C5
		const note3 = t > 0.2 && t < 0.4 ? Math.sin(2 * Math.PI * 659.25 * (t - 0.2)) * Math.exp(-(t - 0.3) * 30) : 0; // E5
		data[i] = (note1 + note2 + note3) * envelope * 0.3;
	}
	return data;
};

// Sound 8: Victory Trumpet
const generateVictoryTrumpet = (_ctx: AudioContext, _duration: number, sampleRate: number, numSamples: number): Float32Array => {
	const data = new Float32Array(numSamples);

	for (let i = 0; i < numSamples; i++) {
		const t = i / sampleRate;
		const envelope = Math.exp(-t * 1.8);
		const freq = 440 + Math.sin(t * 2) * 50; // Vibrato
		const trumpet = Math.sin(2 * Math.PI * freq * t) * envelope;
		const harmonics = Math.sin(2 * Math.PI * freq * 2 * t) * envelope * 0.3;
		data[i] = (trumpet + harmonics) * envelope * 0.35;
	}
	return data;
};

// Sound 9: Cheering Crowd
const generateCheering = (_ctx: AudioContext, _duration: number, sampleRate: number, numSamples: number): Float32Array => {
	const data = new Float32Array(numSamples);

	for (let i = 0; i < numSamples; i++) {
		const t = i / sampleRate;
		const envelope = Math.exp(-t * 0.8);
		// Multiple voice-like frequencies
		const voice1 = Math.sin(2 * Math.PI * (300 + Math.sin(t * 3) * 50) * t) * envelope;
		const voice2 = Math.sin(2 * Math.PI * (400 + Math.sin(t * 4) * 60) * t) * envelope;
		const voice3 = Math.sin(2 * Math.PI * (500 + Math.sin(t * 5) * 70) * t) * envelope;
		const noise = (Math.random() * 2 - 1) * envelope * 0.2;
		data[i] = (voice1 + voice2 + voice3 + noise) * envelope * 0.25;
	}
	return data;
};

// Sound 10: Mixed Celebration (fireworks + confetti + pop)
const generateMixedCelebration = (_ctx: AudioContext, _duration: number, sampleRate: number, numSamples: number): Float32Array => {
	const data = new Float32Array(numSamples);
	let filter = 0;

	for (let i = 0; i < numSamples; i++) {
		const t = i / sampleRate;
		// Fireworks
		const fireworkEnv = Math.exp(-t * 3);
		const whoosh = Math.sin(2 * Math.PI * (200 - t * 100) * t) * fireworkEnv * 0.2;
		const crackle = (Math.random() * 2 - 1) * fireworkEnv * 0.3;
		// Confetti
		const confettiEnv = Math.exp(-t * 2);
		const noise = Math.random() * 2 - 1;
		filter = filter * 0.8 + noise * 0.2;
		const rustle = filter * confettiEnv * 0.2;
		// Pop
		const pop = t < 0.15 ? Math.sin(2 * Math.PI * (1000 - t * 400) * t) * Math.exp(-t * 15) * 0.3 : 0;
		data[i] = (whoosh + crackle + rustle + pop) * 0.4;
	}
	return data;
};

/**
 * Play a random celebration sound from 10 different options
 * Creates variety so the same sound doesn't play every time
 */
export const playCelebrationSound = () => {
	try {
		const ctx = getAudioContext();

		// Resume audio context if suspended (required for user interaction)
		if (ctx.state === "suspended") {
			ctx.resume();
		}

		// Array of sound generators
		const soundGenerators = [generateConfettiPop, generateFireworks, generateBalloonPop, generateCrowdClap, generatePartyHorn, generateCelebrationChime, generateSuccessFanfare, generateVictoryTrumpet, generateCheering, generateMixedCelebration];

		// Randomly select a sound
		const randomIndex = Math.floor(Math.random() * soundGenerators.length);
		const generateSound = soundGenerators[randomIndex];

		// Duration varies by sound type
		const duration = randomIndex === 2 || randomIndex === 3 ? 0.3 : 0.6; // Shorter for pop/clap
		const sampleRate = ctx.sampleRate;
		const numSamples = Math.floor(duration * sampleRate);
		const buffer = ctx.createBuffer(1, numSamples, sampleRate);

		// Generate the selected sound
		const data = generateSound(ctx, duration, sampleRate, numSamples);
		buffer.getChannelData(0).set(data);

		// Create and play the sound
		const source = ctx.createBufferSource();
		const gainNode = ctx.createGain();

		// Set volume (slightly varies by sound type)
		gainNode.gain.value = randomIndex === 3 || randomIndex === 8 ? 0.35 : 0.45; // Quieter for clapping/cheering

		source.buffer = buffer;
		source.connect(gainNode);
		gainNode.connect(ctx.destination);

		// Play the sound
		source.start(0);

		// Clean up after playback
		source.onended = () => {
			source.disconnect();
			gainNode.disconnect();
		};
	} catch (error) {
		// Silently fail if audio is not available or blocked
		console.debug("Could not play celebration sound:", error);
	}
};

/**
 * Play a wheel turning sound (looping)
 * Returns the audio source so it can be stopped when the wheel stops
 */
export const playWheelTurningSound = (): AudioBufferSourceNode | null => {
	try {
		const ctx = getAudioContext();

		if (ctx.state === "suspended") {
			ctx.resume();
		}

		// Create a looping authentic casino roulette wheel sound
		// Longer duration for smoother, more hypnotic sound
		const duration = 1.0; // 1 second per cycle - very smooth
		const sampleRate = ctx.sampleRate;
		const numSamples = Math.floor(duration * sampleRate);
		const buffer = ctx.createBuffer(1, numSamples, sampleRate);
		const data = buffer.getChannelData(0);

		// Initialize filter for ultra-smooth noise
		let smoothFilter = 0;

		// Generate authentic casino roulette sound
		for (let i = 0; i < numSamples; i++) {
			const t = i / sampleRate;

			// Deep, smooth wheel rumble (very low frequency, like a real roulette wheel)
			const wheelFreq = 40 + Math.sin(t * 3) * 10; // Very slow, deep variation
			const wheelRumble = Math.sin(2 * Math.PI * wheelFreq * t) * 0.2;

			// Ball bouncing rhythmically (hitting dividers - creates "tick tick tick" pattern)
			// The ball bounces faster as the wheel slows, but we'll keep it steady for looping
			const ballBounceRate = 8; // 8 bounces per second (like hitting dividers)
			const ballPhase = (t * ballBounceRate) % 1;
			// Create a sharp "tick" sound when ball hits divider
			const ballTick = ballPhase < 0.1 ? Math.sin(2 * Math.PI * 800 * ballPhase * 10) * Math.exp(-ballPhase * 50) * 0.15 : 0;

			// Smooth, continuous rolling sound (ball rolling on track)
			const rollFreq = 120 + Math.sin(t * 5) * 30;
			const ballRoll = Math.sin(2 * Math.PI * rollFreq * t) * 0.1;

			// Ultra-smooth filtered noise for ambient texture (very subtle)
			const noise = Math.random() * 2 - 1;
			smoothFilter = smoothFilter * 0.9 + noise * 0.1; // Very strong low-pass filter
			const ambient = smoothFilter * 0.05; // Very subtle

			// Very smooth envelope for seamless looping
			const envelope = 0.8 + 0.2 * Math.sin((2 * Math.PI * t) / duration);

			// Combine for authentic casino roulette sound
			data[i] = (wheelRumble + ballTick + ballRoll + ambient) * envelope;
		}

		const source = ctx.createBufferSource();
		const gainNode = ctx.createGain();

		// Set volume - casino roulette should be ambient and subtle
		gainNode.gain.value = 0.15; // 15% volume - very subtle, like real casino

		source.buffer = buffer;
		source.loop = true; // Loop the sound
		source.connect(gainNode);
		gainNode.connect(ctx.destination);

		// Play the sound
		source.start(0);

		// Return the source so it can be stopped later
		return source;
	} catch (error) {
		console.debug("Could not play wheel turning sound:", error);
		return null;
	}
};

/**
 * Stop a wheel turning sound
 */
export const stopWheelTurningSound = (source: AudioBufferSourceNode | null) => {
	if (source) {
		try {
			source.stop();
			source.disconnect();
		} catch (error) {
			// Source may have already stopped
			console.debug("Could not stop wheel sound:", error);
		}
	}
};

/**
 * Play a scratching sound (looping)
 * Returns the audio source so it can be stopped when scratching stops
 */
export const playScratchingSound = (): AudioBufferSourceNode | null => {
	try {
		const ctx = getAudioContext();

		if (ctx.state === "suspended") {
			ctx.resume();
		}

		// Create a very smooth, organic scratching sound
		const duration = 0.5; // 500ms per cycle - longer for very smooth sound
		const sampleRate = ctx.sampleRate;
		const numSamples = Math.floor(duration * sampleRate);
		const buffer = ctx.createBuffer(1, numSamples, sampleRate);
		const data = buffer.getChannelData(0);

		// Multiple filters for ultra-smooth, organic texture
		let filter1 = 0;
		let filter2 = 0;
		let filter3 = 0;

		// Generate very smooth, organic scratching sound
		for (let i = 0; i < numSamples; i++) {
			const t = i / sampleRate;

			// Very low frequency organic rumble (like paper/card texture)
			const organicFreq = 80 + Math.sin(t * 2) * 20; // Very slow, natural variation
			const organic = Math.sin(2 * Math.PI * organicFreq * t) * 0.12;

			// Gentle mid-frequency texture (smooth scraping)
			const textureFreq = 200 + Math.sin(t * 4) * 60; // Gentle variation
			const texture = Math.sin(2 * Math.PI * textureFreq * t) * 0.08;

			// Multiple layers of heavily filtered noise for organic texture
			const noise = Math.random() * 2 - 1;

			// Triple filtering for ultra-smooth, organic sound
			filter1 = filter1 * 0.95 + noise * 0.05;
			filter2 = filter2 * 0.92 + filter1 * 0.08;
			filter3 = filter3 * 0.9 + filter2 * 0.1;

			const organicNoise = filter3 * 0.06; // Very subtle, organic texture

			// Very smooth, natural envelope for seamless looping
			const envelope = 0.8 + 0.2 * Math.sin((2 * Math.PI * t) / duration);

			// Combine for very smooth, organic scratching sound
			data[i] = (organic + texture + organicNoise) * envelope;
		}

		const source = ctx.createBufferSource();
		const gainNode = ctx.createGain();

		// Set volume - very subtle, smooth, and organic
		gainNode.gain.value = 0.1; // 10% volume - very subtle and smooth

		source.buffer = buffer;
		source.loop = true; // Loop the sound
		source.connect(gainNode);
		gainNode.connect(ctx.destination);

		// Play the sound
		source.start(0);

		// Return the source so it can be stopped later
		return source;
	} catch (error) {
		console.debug("Could not play scratching sound:", error);
		return null;
	}
};

/**
 * Stop a scratching sound
 */
export const stopScratchingSound = (source: AudioBufferSourceNode | null) => {
	if (source) {
		try {
			source.stop();
			source.disconnect();
		} catch (error) {
			// Source may have already stopped
			console.debug("Could not stop scratching sound:", error);
		}
	}
};

/**
 * Play a simple confetti pop sound
 * Shorter, more subtle sound for confetti bursts
 */
export const playConfettiSound = () => {
	try {
		const ctx = getAudioContext();

		if (ctx.state === "suspended") {
			ctx.resume();
		}

		const duration = 0.15; // 150ms - short pop
		const sampleRate = ctx.sampleRate;
		const numSamples = duration * sampleRate;
		const buffer = ctx.createBuffer(1, numSamples, sampleRate);
		const data = buffer.getChannelData(0);

		// Generate a quick "pop" sound
		for (let i = 0; i < numSamples; i++) {
			const t = i / sampleRate;
			const freq = 800 + t * 200; // Rising frequency
			const envelope = Math.exp(-t * 20); // Quick decay
			data[i] = Math.sin(2 * Math.PI * freq * t) * envelope * 0.15;
		}

		const source = ctx.createBufferSource();
		const gainNode = ctx.createGain();
		gainNode.gain.value = 0.2; // Lower volume for subtle effect

		source.buffer = buffer;
		source.connect(gainNode);
		gainNode.connect(ctx.destination);
		source.start(0);

		source.onended = () => {
			source.disconnect();
			gainNode.disconnect();
		};
	} catch (error) {
		console.debug("Could not play confetti sound:", error);
	}
};
